import { FunctionComponent, useEffect, useRef, useState } from 'react'
import { Grid } from '@material-ui/core'
import { MdCheck } from 'react-icons/md'
import { useHistory } from 'react-router'
import * as Yup from 'yup'

import ContainerPage from 'shared/components/ContainerPageNew'
import RadioButton from 'shared/components/Radio'
import { colors } from 'shared/styles/colors'
import Input from 'shared/components/Input'
import Form from 'shared/components/Form'
import getValidationErrors, {
  IValidationErrors,
} from 'shared/utils/getValidationErrors'
import { cnpjMask, phoneMask } from 'shared/utils/formatters'
import Modal from 'shared/components/Modal'
import Text from 'shared/components/Text'
import Button from 'shared/components/Button'
import SnackBar from 'shared/components/SnackBar'
import LoadingBackdrop from 'shared/components/LoadingBackdrop'
import { ICreateUsuario } from 'modules/user/domain/usecases/CreateUsuarioImpl'
import { useFrotista } from 'modules/user/hooks/useFrotista'

import TransferList from './TransferList'
import { StyledCheck, StyledError, Title } from './styles'
import { FormikProps, FormikValues } from 'formik'
export interface IProps {
  createUsuario: ICreateUsuario
}

interface IFormProps {
  nome: string
  email: string
  accountType: string
  cnpjsSelecionados: string
  telefone: string
}

export interface ICnpjFrotista {
  cnpj: string
  id: number
}

const CreateUsuario: FunctionComponent<IProps> = ({ createUsuario }) => {
  const [accountType, setAccountType] = useState('')
  const [errorMessageForm, setErrorMessageForm] = useState({} as IFormProps)
  const { frotistas } = useFrotista()
  const nome = ''
  const email = ''
  const telefone = ''
  let cnpjsSelecionados = ''
  const formRef = useRef<FormikProps<FormikValues>>()
  const history = useHistory()
  const [showModalSucesso, setShowModalSucesso] = useState(false)
  const [availableCnpjs, setAvailableCnpjs] = useState<ICnpjFrotista[]>([])
  const [searchAvailableCnpjs, setSearchAvailableCnpjs] = useState<
    ICnpjFrotista[]
  >([])

  const [selectedCnpjs, setSelectedCnpjs] = useState<ICnpjFrotista[]>([])
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [loadingBackdrop, setLoadingBackdrop] = useState(false)

  useEffect(() => {
    const cnpjs: ICnpjFrotista[] = []
    frotistas.forEach(frotista => {
      cnpjs.push({ cnpj: cnpjMask(frotista.cnpj), id: frotista.id })
    })
    setAvailableCnpjs(cnpjs)
    setSearchAvailableCnpjs(cnpjs)
  }, [frotistas])

  const clearValidationMessages = () => {
    setErrorMessageForm({
      nome: '',
      email: '',
      accountType: '',
      cnpjsSelecionados: '',
      telefone: '',
    })
  }

  const showValidationErrors = (errors: IValidationErrors) => {
    setErrorMessageForm({
      nome: errors.nome,
      email: errors.email,
      accountType: errors.accountType,
      cnpjsSelecionados: errors.cnpjsSelecionados,
      telefone: errors.telefone,
    })
  }

  const handleSubmit = async () => {
    const { nome, email, telefone } = formRef.current.values
    cnpjsSelecionados = selectedCnpjs.length === 0 ? '' : 'ok'
    const frotistas_ids = []
    selectedCnpjs.forEach(cnpj => frotistas_ids.push(cnpj.id))

    try {
      const schema = Yup.object().shape({
        nome: Yup.string().required('Nome é obrigatório!'),
        email: Yup.string()
          .email('E-mail inválido!')
          .required('Email é obrigatório!'),
        accountType: Yup.string().required('Tipo de conta é obrigatório!'),
        cnpjsSelecionados: Yup.string().required(
          'Deve selecionar ao menos um CNPJ',
        ),
        telefone: Yup.string()
          .required('Telefone é obrigatório!')
          .length(15, 'Telefone é obrigatório!'),
      })

      await schema.validate(
        {
          nome,
          email,
          accountType,
          cnpjsSelecionados,
          telefone,
        },
        {
          abortEarly: false,
        },
      )
      clearValidationMessages()

      setLoadingBackdrop(true)
      await createUsuario.execute({
        nome,
        email,
        perfil: accountType,
        telefone,
        frotistas_ids,
      })
      setLoadingBackdrop(false)
      setShowModalSucesso(true)
    } catch (error: unknown) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error)
        showValidationErrors(errors)
      } else {
        setLoadingBackdrop(false)
        if (error instanceof Error) {
          setErrorMessage(error.message)
        }
        setShowError(true)
      }
    }
  }

  return (
    <ContainerPage
      breadCrumb="Home / Gerenciar Conta / Usuários / Novo usuário"
      titleNextButton="Salvar"
      onClickNextButton={handleSubmit}
      titleBackButton="Voltar"
      onClickBackButton={() => history.goBack()}
      iconButtonNext={MdCheck}
      title="Insira os dados:"
      titleHeader="Novo Usuário"
    >
      <Form
        validationSchema={() => ({
          nome: Yup.string().required(),
          email: Yup.string().email().required(),
          accountType: Yup.string().required(),
          cnpjsSelecionados: Yup.string().required(),
          telefone: Yup.string().required(),
        })}
        onSubmit={undefined}
        innerRef={formRef}
        initialValues={{
          nome,
          email,
          accountType,
          cnpjsSelecionados,
          telefone,
        }}
      >
        {formControl => (
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <Input
                label="Nome"
                placeholder="Digite o nome"
                name="nome"
                type="text"
                formControl={formControl}
                errorMessage={errorMessageForm?.nome}
              />
            </Grid>
            <Grid item xs={3}>
              <Input
                label="Email"
                placeholder="Digite o email"
                name="email"
                mask={value => value.toLowerCase()}
                type="text"
                formControl={formControl}
                errorMessage={errorMessageForm?.email}
              />
            </Grid>
            <Grid item xs={3}>
              <Input
                label="Telefone celular"
                placeholder="Digite o telefone"
                name="telefone"
                type="text"
                maxLength={15}
                mask={value => phoneMask(value)}
                formControl={formControl}
                errorMessage={errorMessageForm?.telefone}
              />
            </Grid>
            <Grid item xs={12} style={{ marginTop: -20 }}>
              <Title>Tipo de conta:</Title>
              <StyledError style={{ marginTop: -20 }}>
                {errorMessageForm.accountType}
              </StyledError>
            </Grid>

            <Grid container spacing={3} style={{ margin: '-40px 0px 0px 0px' }}>
              <Grid item xs={3}>
                <Grid xs={12}>
                  <RadioButton
                    id="OPERACIONAL"
                    label="Operacional"
                    name="accountType"
                    value="OPERACIONAL"
                    onChange={setAccountType}
                  />
                  <div style={{ margin: '25px', fontSize: 12 }}>
                    sem acesso a pagamentos e gerenciar conta
                  </div>
                </Grid>
              </Grid>
              <Grid item xs={3}>
                <Grid xs={12}>
                  <RadioButton
                    id="ADMIN"
                    label="Administrador"
                    name="accountType"
                    value="ADMIN"
                    onChange={setAccountType}
                  />
                  <div style={{ margin: '25px', fontSize: 12 }}>
                    acesso total
                  </div>
                </Grid>
              </Grid>
            </Grid>

            <Grid container style={{ margin: '0px 10px' }}>
              <Title>Vincular CNPJ:</Title>
              <StyledError style={{ marginTop: -30 }}>
                {errorMessageForm.cnpjsSelecionados}
              </StyledError>
            </Grid>
            <TransferList
              availableCnpjs={availableCnpjs}
              setAvailableCnpjs={setAvailableCnpjs}
              selectedCnpjs={selectedCnpjs}
              setSelectedCnpjs={setSelectedCnpjs}
              searchAvailableCnpjs={searchAvailableCnpjs}
              setSearchAvailableCnpjs={setSearchAvailableCnpjs}
            />
          </Grid>
        )}
      </Form>

      {showModalSucesso && (
        <Modal
          openModal={showModalSucesso}
          size="small"
          showCloseButton={false}
          onCloseModal={() => setShowModalSucesso(false)}
        >
          <StyledCheck style={{ marginBottom: 20, marginTop: 40 }} />
          <Text
            style={{ fontSize: 20, marginBottom: 20, fontWeight: 'bold' }}
            color={colors.blue}
          >
            Cadastro realizado com sucesso
          </Text>

          <Text style={{ margin: '20px' }}>
            O usuário cadastrado receberá um email para acessar o seu perfil.
          </Text>
          <Button
            color={colors.green}
            onClick={() => {
              history.push('/usuarios-vinculados')
              setShowModalSucesso(false)
            }}
          >
            <MdCheck />
            Ok
          </Button>
        </Modal>
      )}

      <SnackBar
        open={showError}
        setOpen={setShowError}
        severity="error"
        message={errorMessage}
        horizontal="center"
      />

      <LoadingBackdrop open={loadingBackdrop} color={colors.green} />
    </ContainerPage>
  )
}

export default CreateUsuario
